import { DynamoClient } from '../abstractClient';
import { ReturnValue } from '@aws-sdk/client-dynamodb';
import {
  AddCoveragePayload,
  AddDiscountPayload,
  AddDriverPayload,
  AddVehiclePayload,
  CreatePolicyPayload,
  CategorizedCoveragePayload,
  CreateCoverageCategoryPayload,
  DeleteCoveragePayload,
  DeleteDiscountPayload,
  ResourceType,
  UpdateCoverageCategoryPayload,
  UpdateCoveragePayload,
  UpdateDriverPayload,
  UpdateVehiclePayload,
  PrimaryAddress,
  PolicyResource,
} from './policyClient.types';
import {
  LabeledDataPolicyResource,
  LabeledDataPolicyResponse,
} from '../labeledData/labeledDataClient.types';
import { TableName } from '../types';

export class PolicyClient extends DynamoClient {
  tableName: string;

  constructor({
    dbConfig,
    tableName,
  }: {
    tableName: TableName;
    dbConfig: {
      region: string;
      credentials: {
        secretAccessKey: string;
        accessKeyId: string;
      };
    };
  }) {
    super(dbConfig);
    this.tableName = tableName;
  }

  async createPolicy(data: CreatePolicyPayload) {
    const input = {
      TableName: this.tableName,
      Item: {
        resourceType: ResourceType.Policy,
        resourceId: data.policyId,
        policyUUID: data.policyUUID,
        blocks: data.blocks,
      },
      ConditionExpression:
        'attribute_not_exists(resourceType) AND attribute_not_exists(resourceId)',
    };
    try {
      await this.putItem(input);
    } catch (e: any) {
      if (e.__type.includes('ConditionalCheckFailedException')) {
        throw new Error('Record already exists');
      } else {
        throw e;
      }
    }
  }

  async updatePolicyJobId(policyId: string, JobId: string) {
    const input: any = {
      TableName: this.tableName,
      Key: {
        resourceType: ResourceType.Policy,
        resourceId: policyId,
      },
      UpdateExpression: 'SET decPageJobId=:jobId',
      ExpressionAttributeValues: { ':jobId': JobId },
      ReturnValues: ReturnValue.ALL_NEW,
    };

    const policy = await this.updateItem(input);
    if (!policy) throw new Error('No returned attributes');
    return policy;
  }

  async getPolicy(policyId: string) {
    const input = {
      TableName: this.tableName,
      Key: {
        resourceType: ResourceType.Policy,
        resourceId: policyId,
      },
    };

    const policy = await this.getItem(input);

    if (!policy) throw new Error('No policy found');

    return policy;
  }

  async getPolicyDetails(policyId: string): Promise<LabeledDataPolicyResponse> {
    const policy = await this.getPolicy(policyId);
    const vehicles = await this.getVehicles({ policyId });
    const drivers = await this.getDrivers({ policyId });

    return { ...policy, vehicles, drivers } as LabeledDataPolicyResponse;
  }

  async getPolicyList() {
    const input = {
      TableName: 'labeled_data',
      KeyConditionExpression: 'resourceType = :policy',
      ExpressionAttributeValues: { ':policy': ResourceType.Policy },
    };

    const policies = await this.queryItems(input);
    if (!policies) throw new Error('No policies found');
    return policies;
  }

  // Adds empty address field to policy.
  // Needed because dynamoDB cannot do an update on an
  // object's attributes if that object does not exist yet
  async addAddressToPolicy(policyId: string) {
    const input: any = {
      TableName: this.tableName,
      Key: {
        resourceType: ResourceType.Policy,
        resourceId: policyId,
      },
      UpdateExpression: 'SET #address=:address',
      ExpressionAttributeValues: {
        ':address': {},
      },
      ExpressionAttributeNames: {
        '#address': 'address',
      },
      ReturnValues: ReturnValue.ALL_NEW,
    };

    await this.updateItem(input);
  }

  async updatePolicyAddress(
    policyId: string,
    address: PrimaryAddress,
  ): Promise<PolicyResource> {
    const input: any = {
      TableName: this.tableName,
      Key: {
        resourceType: ResourceType.Policy,
        resourceId: policyId,
      },
      UpdateExpression: 'SET ',
      ExpressionAttributeValues: {},
      ExpressionAttributeNames: {},
      ReturnValues: ReturnValue.ALL_NEW,
    };

    Object.entries(address).forEach(([label, value], i) => {
      if (value !== undefined) {
        input.UpdateExpression += ` address.#${label}=:value${i},`;
        input.ExpressionAttributeValues[`:value${i}`] = value;
        input.ExpressionAttributeNames[`#${label}`] = label;
      }
    });

    input.UpdateExpression = input.UpdateExpression.slice(0, -1);

    try {
      const policy = await this.updateItem(input);
      if (!policy) throw new Error('No returned attributes');
      return policy as PolicyResource;
    } catch (e: any) {
      if (
        e.message ===
        'The document path provided in the update expression is invalid for update'
      ) {
        await this.addAddressToPolicy(policyId);
        return await this.updatePolicyAddress(policyId, address);
      } else {
        throw e;
      }
    }
  }

  async addVehicleToPolicy(data: AddVehiclePayload) {
    const input: any = {
      TableName: this.tableName,
      Item: {
        resourceType: ResourceType.Vehicle,
        resourceId: this.generateSecondaryResourceId(data.policyId),
      },
      ConditionExpression:
        'attribute_not_exists(resourceType) AND attribute_not_exists(resourceId) AND attribute_not_exists(vin)',
      ReturnValues: ReturnValue.ALL_OLD,
    };

    Object.entries(data).forEach(([label, value]) => {
      if (value && label !== 'policyId' && label !== 'resourceId') {
        input.Item[label] = value;
      }
    });

    try {
      return await this.putItem(input);
    } catch (e: any) {
      if (e.__type.includes('ConditionalCheckFailedException')) {
        throw new Error('Record already exists');
      } else {
        throw e;
      }
    }
  }

  async updateVehicle(vehicleId: string, data: UpdateVehiclePayload) {
    const input: any = {
      TableName: this.tableName,
      Key: {
        resourceType: ResourceType.Vehicle,
        resourceId: vehicleId,
      },
      UpdateExpression: 'SET',
      ExpressionAttributeValues: {},
      ExpressionAttributeNames: {},
      ReturnValues: ReturnValue.ALL_NEW,
    };

    Object.entries(data).forEach(([label, value], i) => {
      if (value) {
        // 'name' and 'year' are a reserved words, so they require special handling
        if (label === 'name') {
          input.UpdateExpression += ` #N=:value${i},`;
          input.ExpressionAttributeValues[`:value${i}`] = value;
          input.ExpressionAttributeNames['#N'] = 'name';
        } else if (label === 'year') {
          input.UpdateExpression += ` #Y=:value${i},`;
          input.ExpressionAttributeValues[`:value${i}`] = value;
          input.ExpressionAttributeNames['#Y'] = 'year';
        } else {
          input.UpdateExpression += ` ${label}=:value${i},`;
          input.ExpressionAttributeValues[`:value${i}`] = value;
        }
      }
    });

    input.UpdateExpression = input.UpdateExpression.slice(0, -1);

    const vehicle = await this.updateItem(input);
    if (!vehicle) throw new Error('No returned attributes');
    return vehicle;
  }

  async getVehicles(data: { policyId: string }) {
    const input = {
      TableName: this.tableName,
      KeyConditionExpression:
        'resourceType = :resourceType AND begins_with(resourceId, :policyId)',
      ExpressionAttributeValues: {
        ':policyId': data.policyId,
        ':resourceType': ResourceType.Vehicle,
      },
    };

    const vehicles = await this.queryItems(input);
    if (!vehicles) return [];
    return vehicles;
  }

  async deleteVehicle(vehicleId: string) {
    const input = {
      TableName: this.tableName,
      Key: {
        resourceType: ResourceType.Vehicle,
        resourceId: vehicleId,
      },
      ReturnValues: ReturnValue.ALL_OLD,
    };

    const vehicle = await this.deleteItem(input);
    if (!vehicle) throw new Error('No returned attributes');
    return vehicle;
  }

  async addCoverageCategoryToResource(data: CreateCoverageCategoryPayload) {
    const input = {
      TableName: this.tableName,
      Key: {
        resourceType: data.resourceType,
        resourceId: data.resourceId,
      },
      UpdateExpression:
        'SET #c = list_append(if_not_exists(#c, :empty_list), :coverage)',
      ExpressionAttributeValues: {
        ':coverage': [
          {
            categoryName: data.categoryName,
            ...(data.premium && {
              premium: data.premium,
            }),
            coverages: [],
          },
        ],
        ':empty_list': [],
      },
      ExpressionAttributeNames: {
        '#c': 'coverages',
      },
      ReturnValues: ReturnValue.ALL_NEW,
    };

    const resource = await this.updateItem(input);
    if (!resource) throw new Error('No returned attributes');
    return resource;
  }

  async updateCoverageCategory(data: UpdateCoverageCategoryPayload) {
    const input: any = {
      TableName: this.tableName,
      Key: {
        resourceType: data.resourceType,
        resourceId: data.resourceId,
      },
      UpdateExpression: 'SET',
      ExpressionAttributeValues: {},
      ReturnValues: ReturnValue.ALL_NEW,
    };

    if (data.categoryName) {
      input.UpdateExpression += ` coverages[${data.coverageId}].categoryName = :categoryName,`;
      input.ExpressionAttributeValues[':categoryName'] = data.categoryName;
    }

    if (data.premium) {
      input.UpdateExpression += ` coverages[${data.coverageId}].premium = :premium,`;
      input.ExpressionAttributeValues[':premium'] = data.premium;
    }

    input.UpdateExpression = input.UpdateExpression.slice(0, -1);

    const resource = await this.updateItem(input);
    if (!resource) throw new Error('No returned attributes');
    return resource;
  }

  async updateCategorizedCoverages(data: CategorizedCoveragePayload) {
    const input = {
      TableName: this.tableName,
      Key: {
        resourceType: data.resourceType,
        resourceId: data.resourceId,
      },
      UpdateExpression: `SET coverages[${data.coverageId}] = :categorizedCoverage`,
      ExpressionAttributeValues: {
        ':categorizedCoverage': data.categorizedCoverage,
      },
      ReturnValues: ReturnValue.ALL_NEW,
    };

    const resource = await this.updateItem(input);
    if (!resource) throw new Error('No returned attributes');
    return resource;
  }

  async addCoverageToResource(data: AddCoveragePayload) {
    const input = {
      TableName: this.tableName,
      Key: {
        resourceType: data.resourceType,
        resourceId: data.resourceId,
      },
      UpdateExpression:
        'SET #c = list_append(if_not_exists(#c, :empty_list), :coverage)',
      ExpressionAttributeValues: {
        ':coverage': [
          {
            name: data.coverage.name,
            ...(data.coverage.limit && {
              limit: data.coverage.limit,
            }),
            ...(data.coverage.deductible && {
              deductible: data.coverage.deductible,
            }),
            ...(data.coverage.premium && {
              premium: data.coverage.premium,
            }),
          },
        ],
        ':empty_list': [],
      },
      ExpressionAttributeNames: {
        '#c': 'coverages',
      },
      ReturnValues: ReturnValue.ALL_NEW,
    };

    const resource = await this.updateItem(input);
    if (!resource) throw new Error('No returned attributes');
    return resource;
  }

  async deleteResourceCoverage(data: DeleteCoveragePayload) {
    const input = {
      TableName: this.tableName,
      Key: {
        resourceType: data.resourceType,
        resourceId: data.resourceId,
      },
      UpdateExpression: `REMOVE coverages[${data.coverageId}]`,
      ReturnValues: ReturnValue.ALL_NEW,
    };

    const resource = await this.updateItem(input);
    if (!resource) throw new Error('No returned attributes');
    return resource;
  }

  async updateResourceCoverage(data: UpdateCoveragePayload) {
    const input = {
      TableName: this.tableName,
      Key: {
        resourceType: data.resourceType,
        resourceId: data.resourceId,
      },
      UpdateExpression: `SET coverages[${data.coverageId}] = :coverage`,
      ExpressionAttributeValues: {
        ':coverage': {
          name: data.coverage.name,
          ...(data.coverage.limit && {
            limit: data.coverage.limit,
          }),
          ...(data.coverage.deductible && {
            deductible: data.coverage.deductible,
          }),
          ...(data.coverage.premium && {
            premium: data.coverage.premium,
          }),
        },
      },
      ReturnValues: ReturnValue.ALL_NEW,
    };

    const resource = await this.updateItem(input);
    if (!resource) throw new Error('No returned attributes');
    return resource;
  }

  async addDriverToPolicy(data: AddDriverPayload) {
    const input: any = {
      TableName: this.tableName,
      Item: {
        resourceType: ResourceType.Driver,
        resourceId: this.generateSecondaryResourceId(data.policyId),
        firstName: data.firstName,
        lastName: data.lastName,
      },
      ConditionExpression:
        'attribute_not_exists(resourceType) AND attribute_not_exists(resourceId)',
      ReturnValues: ReturnValue.ALL_OLD,
    };

    if (data.driverLicenseCode) {
      input.Item.driverLicenseCode = data.driverLicenseCode;
    }

    if (data.usStateCode) {
      input.Item.usStateCode = data.usStateCode;
    }

    if (data.dateOfBirth) {
      input.Item.dateOfBirth = data.dateOfBirth;
    }

    try {
      return await this.putItem(input);
    } catch (e: any) {
      if (e.__type.includes('ConditionalCheckFailedException')) {
        throw new Error('Record already exists');
      } else {
        throw e;
      }
    }
  }

  async updateDriver(driverId: string, data: UpdateDriverPayload) {
    const input: any = {
      TableName: this.tableName,
      Key: {
        resourceType: ResourceType.Driver,
        resourceId: driverId,
      },
      UpdateExpression: 'SET',
      ExpressionAttributeValues: {},
      ReturnValues: ReturnValue.ALL_NEW,
    };

    Object.entries(data).forEach(([label, value], i) => {
      if (value) {
        input.UpdateExpression += ` ${label}=:value${i},`;
        input.ExpressionAttributeValues[`:value${i}`] = value;
      }
    });
    input.UpdateExpression = input.UpdateExpression.slice(0, -1);

    const resource = await this.updateItem(input);
    if (!resource) throw new Error('No returned attributes');
    return resource;
  }

  async getDrivers(data: { policyId: string }) {
    const input = {
      TableName: this.tableName,
      KeyConditionExpression:
        'resourceType = :resourceType AND begins_with(resourceId, :policyId)',
      ExpressionAttributeValues: {
        ':policyId': data.policyId,
        ':resourceType': ResourceType.Driver,
      },
    };

    const drivers = await this.queryItems(input);
    if (!drivers) return [];
    return drivers;
  }

  async deleteDriver(driverId: string) {
    const input = {
      TableName: this.tableName,
      Key: {
        resourceType: ResourceType.Driver,
        resourceId: driverId,
      },
      ReturnValues: ReturnValue.ALL_OLD,
    };

    const driver = await this.deleteItem(input);
    if (!driver) throw new Error('No returned attributes');
    return driver;
  }

  async addDiscountToResource(
    resourceType: ResourceType,
    data: AddDiscountPayload,
  ) {
    const input: any = {
      TableName: this.tableName,
      Key: {
        resourceType: resourceType,
        resourceId: data.resourceId,
      },
      UpdateExpression:
        'SET #d = list_append(if_not_exists(#d, :empty_list), :discounts)',
      ExpressionAttributeValues: {
        ':empty_list': [],
      },
      ExpressionAttributeNames: {
        '#d': 'discounts',
      },
      ReturnValues: ReturnValue.ALL_NEW,
    };

    // Handles bulk add vs singular add
    if ('discounts' in data) {
      input.ExpressionAttributeValues[':discounts'] = data.discounts
        .split(',')
        .map((discountName) => {
          return {
            name: discountName,
          };
        });
    } else {
      input.ExpressionAttributeValues[':discounts'] = [
        {
          name: data.discount.name,
          ...(data.discount.amount && {
            amount: data.discount.amount,
          }),
        },
      ];
    }

    const resource = await this.updateItem(input);
    if (!resource) throw new Error('No returned attributes');
    return resource;
  }

  async deleteResourceDiscount(
    resourceType: ResourceType,
    data: DeleteDiscountPayload,
  ) {
    const input = {
      TableName: this.tableName,
      Key: {
        resourceType: resourceType,
        resourceId: data.resourceId,
      },
      UpdateExpression: `REMOVE discounts[${data.discountId}]`,
      ReturnValues: ReturnValue.ALL_NEW,
    };

    const resource = await this.updateItem(input);
    if (!resource) throw new Error('No returned attributes');
    return resource;
  }
}
